import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import BracketHeightRangePanel from '../components/bracket/BracketHeightRangePanel';
import BracketResultsPanel from '../components/bracket/BracketResultsPanel';
import BracketSetView from '../components/bracket/BracketSetView';
import CourtPickerModal from '../components/bracket/CourtPickerModal';
import LiveMatchPanel from '../components/bracket/LiveMatchPanel';
import MatchScoreModal from '../components/bracket/MatchScoreModal';
import TournamentSelector from '../components/bracket/TournamentSelector';
import { getApiErrorMessage } from '../api/client';
import { useI18n } from '../i18n';
import { translateDivisionLabel, translateRoundLabel } from '../i18n/labels';
import {
  fetchTournamentParticipants,
  fetchTournaments,
} from '../api/competitionApplication';
import { endLiveMatch, fetchLiveMatches, startLiveMatch } from '../api/competitionLiveMatch';
import {
  fetchBracket,
  fetchBracketResults,
  finalizeBracket,
  reopenBracket,
  saveBracket,
} from '../api/competitionBracket';
import { formatEventDate, normalizeCompetitionList } from '../utils/competitionList';
import { computeBracketStats, getMatchDisplayNumber, isMatchList } from '../utils/bracket';
import { BRACKET_DISPLAY_POLL_MS, openBracketDisplayWindow } from '../utils/bracketDisplaySync';
import {
  fromBracketResponse,
  getBracketParticipantDiff,
  toBracketPayload,
} from '../utils/bracketStorage';
import {
  applyHeightRangesToBracketSet,
  BRACKET_FORMATS,
  BRACKET_MODES,
  canFinalizeBracketSet,
  generateBracketSet,
  getAllBracketSetDivisions,
  getBracketFormatLabelKey,
  getBracketSetDivisions,
  getBracketSetSummaryLabel,
  isMatchFormat,
  selectBracketSetWinner,
  setBracketSetMatchResult,
  setBracketSetSelectedHeightRange,
  swapBracketSetSlots,
  updateBracketSetDivision,
} from '../utils/bracketSet';
import { normalizeParticipantList } from '../utils/participantList';

const AUTOSAVE_DELAY_MS = 400;
/** 코트 선택 창에 보여줄 코트 수 (이 브라우저에 저장) */
const COURT_COUNT_KEY = 'tournament-court-count';
const DEFAULT_COURT_COUNT = 2;

function readCourtCount() {
  try {
    const value = Number(localStorage.getItem(COURT_COUNT_KEY));
    return Number.isInteger(value) && value >= 1 ? value : DEFAULT_COURT_COUNT;
  } catch {
    return DEFAULT_COURT_COUNT;
  }
}

const SAVE_STATE_KEYS = {
  saving: 'tournament.admin.saveState.saving',
  saved: 'tournament.admin.saveState.saved',
  synced: 'tournament.admin.saveState.synced',
};

function BracketPage() {
  const { t } = useI18n();
  /** 오류 상태 { key, error? } → 현재 언어 문구 (서버 메시지는 영어 화면에서 key 문구로 대체) */
  const errorText = (state) => {
    if (!state) return null;
    return state.error ? getApiErrorMessage(state.error, t(state.key)) : t(state.key);
  };

  const [tournaments, setTournaments] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  const [tournamentsError, setTournamentsError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState(null);

  const [bracketData, setBracketData] = useState(null);
  /** 서버 대진표 상태: null(미저장) / IN_PROGRESS / FINISHED */
  const [bracketStatus, setBracketStatus] = useState(null);
  const [bracketLoading, setBracketLoading] = useState(false);
  const [bracketError, setBracketError] = useState(null);
  /** 참가자 변동 알림: { added, removed } 또는 null */
  const [bracketNotice, setBracketNotice] = useState(null);
  const [saveState, setSaveState] = useState('idle');
  const [saveError, setSaveError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [finalizing, setFinalizing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSwap, setSelectedSwap] = useState(null);
  /** 점수 입력 중인 경기 { divisionKey, divisionLabel, roundIndex, matchIndex } */
  const [scoreTarget, setScoreTarget] = useState(null);
  /** VR 코트별 진행 중인 경기 (모든 대회) */
  const [liveMatches, setLiveMatches] = useState([]);
  /** 코트를 고르는 중인 경기 { divisionKey, roundIndex, matchIndex } */
  const [courtTarget, setCourtTarget] = useState(null);
  const [courtCount, setCourtCount] = useState(readCourtCount);
  const [results, setResults] = useState([]);

  const selectedIdRef = useRef(null);
  const versionRef = useRef(null);
  const savedKeyRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve(true));
  const editSnapshotRef = useRef(null);
  const bracketDataRef = useRef(null);

  const loadTournaments = useCallback(async () => {
    setTournamentsLoading(true);
    setTournamentsError(null);
    try {
      const data = await fetchTournaments();
      const list = normalizeCompetitionList(data);
      setTournaments(list);
      if (list.length === 0) {
        setTournamentsError({ key: 'tournament.errors.noTournaments' });
      }
    } catch (error) {
      setTournamentsError({ key: 'tournament.errors.loadTournaments', error });
      setTournaments([]);
    } finally {
      setTournamentsLoading(false);
    }
  }, []);

  const loadParticipants = useCallback(async (tournamentId) => {
    setParticipantsLoading(true);
    setParticipantsError(null);
    setParticipants([]);
    try {
      const data = await fetchTournamentParticipants(tournamentId);
      const list = normalizeParticipantList(data);
      setParticipants(list);
      if (list.length === 0) {
        setParticipantsError({ key: 'tournament.errors.noParticipants' });
      }
    } catch (error) {
      setParticipantsError({ key: 'tournament.errors.loadParticipants', error });
      setParticipants([]);
    } finally {
      setParticipantsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  const handleSelectTournament = (tournamentId) => {
    selectedIdRef.current = tournamentId;
    setSelectedId(tournamentId);
    setParticipants([]);
    setParticipantsError(null);
    setBracketData(null);
    setEditMode(false);
    setSelectedSwap(null);
    loadParticipants(tournamentId);
  };

  const isSelected = (tournamentId) =>
    selectedId != null && String(selectedId) === String(tournamentId);

  const selectedTournament = tournaments.find((item) => isSelected(item.id));
  const isFinished = bracketStatus === 'FINISHED';
  const stats = useMemo(() => computeBracketStats(participants), [participants]);
  const divisions = useMemo(
    () => getBracketSetDivisions(bracketData),
    [bracketData]
  );
  const canFinalize = useMemo(() => canFinalizeBracketSet(bracketData), [bracketData]);
  /** 1:1 경기 방식 (토너먼트가 아님) */
  const matchFormat = isMatchFormat(bracketData);

  // 서버에 저장된 대진표 불러오기 (없으면 새로 생성 → 자동 저장)
  useEffect(() => {
    if (selectedId == null || participants.length < 2) {
      setBracketData(null);
      setBracketStatus(null);
      setBracketNotice(null);
      setBracketError(null);
      versionRef.current = null;
      savedKeyRef.current = null;
      return undefined;
    }

    let cancelled = false;
    setBracketLoading(true);
    setBracketError(null);
    setSaveError(null);
    setSaveState('idle');

    fetchBracket(selectedId)
      .then((data) => {
        if (cancelled) return;
        if (data) {
          const bracketSet = fromBracketResponse(data, participants);
          const diff = getBracketParticipantDiff(data, participants);
          const changed = diff.added > 0 || diff.removed > 0;
          versionRef.current = data.version;
          // 참가자 변동이 있으면 정리된 대진표를 다시 저장하도록 저장 기준을 비운다
          savedKeyRef.current = changed ? null : JSON.stringify(toBracketPayload(bracketSet));
          setBracketStatus(data.status);
          setBracketNotice(changed ? { added: diff.added, removed: diff.removed } : null);
          setBracketData(bracketSet);
        } else {
          versionRef.current = null;
          savedKeyRef.current = null;
          setBracketStatus(null);
          setBracketNotice(null);
          setBracketData(generateBracketSet(participants));
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setBracketError({ key: 'tournament.errors.loadBracket', error });
        setBracketData(null);
      })
      .finally(() => {
        if (!cancelled) setBracketLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [participants, selectedId, reloadKey]);

  /** 대진표 저장 (순서대로 한 건씩). 성공하면 true */
  const persistBracket = useCallback((tournamentId, bracketSet) => {
    const payload = toBracketPayload(bracketSet);
    const key = JSON.stringify(payload);

    const run = async () => {
      if (tournamentId !== selectedIdRef.current) return false;
      if (key === savedKeyRef.current) return true;

      setSaveState('saving');
      try {
        const saved = await saveBracket(tournamentId, { ...payload, version: versionRef.current });
        if (tournamentId !== selectedIdRef.current) return false;
        versionRef.current = saved.version;
        savedKeyRef.current = key;
        setBracketStatus(saved.status);
        setSaveState('saved');
        setSaveError(null);
        return true;
      } catch (error) {
        if (tournamentId !== selectedIdRef.current) return false;
        setSaveState('error');
        setSaveError({ key: 'tournament.errors.saveBracket', error });
        if (error.response?.status === 409) {
          alert(getApiErrorMessage(error, t('tournament.errors.saveBracket')));
          setEditMode(false);
          setSelectedSwap(null);
          setReloadKey((k) => k + 1);
        }
        return false;
      }
    };

    const next = saveQueueRef.current.then(run);
    saveQueueRef.current = next.catch(() => false);
    return next;
  }, [t]);

  // 진행 중 변경(승자 선택 등)은 자동 저장. 편집 모드는 「저장」 버튼으로 저장.
  useEffect(() => {
    if (!bracketData || selectedId == null || editMode || bracketLoading || isFinished) {
      return undefined;
    }
    const timer = setTimeout(() => persistBracket(selectedId, bracketData), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [bracketData, selectedId, editMode, bracketLoading, isFinished, persistBracket]);

  useEffect(() => {
    bracketDataRef.current = bracketData;
  }, [bracketData]);

  // VR 기기 등 다른 곳에서 저장된 결과를 주기적으로 반영한다.
  // 편집 중이거나 점수 입력 창이 열려 있거나 아직 저장 안 된 변경이 있으면 건너뛴다.
  useEffect(() => {
    if (selectedId == null || editMode || scoreTarget || bracketLoading || participants.length < 2) {
      return undefined;
    }
    const tournamentId = selectedId;
    const timer = setInterval(async () => {
      try {
        const data = await fetchBracket(tournamentId);
        if (!data || tournamentId !== selectedIdRef.current) return;
        if (versionRef.current == null || data.version <= versionRef.current) return;
        const current = bracketDataRef.current;
        if (!current || JSON.stringify(toBracketPayload(current)) !== savedKeyRef.current) return;

        const next = fromBracketResponse(data, participants);
        versionRef.current = data.version;
        savedKeyRef.current = JSON.stringify(toBracketPayload(next));
        setBracketStatus(data.status);
        setBracketData(next);
        setSaveState('synced');
      } catch {
        // 다음 주기에 다시 시도
      }
    }, BRACKET_DISPLAY_POLL_MS);
    return () => clearInterval(timer);
  }, [selectedId, editMode, scoreTarget, bracketLoading, participants]);

  const handleSelectWinner = (divisionKey, roundIndex, matchIndex, player) => {
    if (editMode || isFinished) return;
    setBracketData((prev) =>
      selectBracketSetWinner(prev, divisionKey, roundIndex, matchIndex, player)
    );
  };

  // 결과가 확정되면 순위표 불러오기
  useEffect(() => {
    if (selectedId == null || !isFinished) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    fetchBracketResults(selectedId)
      .then((data) => {
        if (!cancelled) setResults(data ?? []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, isFinished]);

  // VR 코트별 진행 중인 경기: 접속 여부와 점수를 주기적으로 갱신
  const loadLiveMatches = useCallback(
    () =>
      fetchLiveMatches()
        .then(setLiveMatches)
        .catch(() => {}),
    []
  );

  useEffect(() => {
    loadLiveMatches();
    const timer = setInterval(loadLiveMatches, BRACKET_DISPLAY_POLL_MS);
    return () => clearInterval(timer);
  }, [loadLiveMatches]);

  const handleChangeCourtCount = (count) => {
    setCourtCount(count);
    try {
      localStorage.setItem(COURT_COUNT_KEY, String(count));
    } catch {
      // ignore storage errors
    }
  };

  /** 「▶ 경기 시작」 → 코트 선택 창 */
  const handleStartLive = (divisionKey, roundIndex, matchIndex) => {
    if (editMode || isFinished || selectedId == null) return;
    setCourtTarget({ divisionKey, roundIndex, matchIndex });
  };

  const handlePickCourt = async (court, busy) => {
    if (!courtTarget || selectedId == null) return;
    if (
      busy &&
      !window.confirm(
        t('tournament.admin.confirm.switchLiveMatch', {
          court,
          no: busy.matchNumber,
          round: translateRoundLabel(busy.roundLabel),
        })
      )
    ) {
      return;
    }
    const { divisionKey, roundIndex, matchIndex } = courtTarget;
    setCourtTarget(null);
    // 서버에 저장된 대진표 기준으로 시작하므로, 저장 안 된 변경이 있으면 먼저 저장
    if (!(await persistBracket(selectedId, bracketData))) return;
    try {
      await startLiveMatch({ court, tournamentId: selectedId, divisionKey, roundIndex, matchIndex });
    } catch (error) {
      alert(getApiErrorMessage(error, t('tournament.errors.startMatch')));
    }
    loadLiveMatches();
  };

  const handleEndLive = async (court) => {
    if (!window.confirm(t('tournament.admin.confirm.endLiveMatch', { court }))) {
      return;
    }
    try {
      await endLiveMatch(court);
    } catch (error) {
      alert(getApiErrorMessage(error, t('tournament.errors.endMatch')));
    }
    loadLiveMatches();
  };

  const courtTargetDivision = courtTarget
    ? getAllBracketSetDivisions(bracketData).find((d) => d.key === courtTarget.divisionKey)
    : null;
  const courtTargetTitle = courtTargetDivision
    ? [
        courtTargetDivision.label && translateDivisionLabel(courtTargetDivision.label),
        t('tournament.admin.scoreModalTitle', {
          no: getMatchDisplayNumber(courtTargetDivision.bracket, courtTarget.roundIndex, courtTarget.matchIndex),
          round: translateRoundLabel(courtTargetDivision.bracket.rounds[courtTarget.roundIndex]?.title),
        }),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const handleEditScore = (divisionKey, divisionLabel, roundIndex, matchIndex) => {
    if (editMode || isFinished) return;
    setScoreTarget({ divisionKey, divisionLabel, roundIndex, matchIndex });
  };

  const handleSaveScore = (result) => {
    if (!scoreTarget) return;
    const { divisionKey, roundIndex, matchIndex } = scoreTarget;
    setBracketData((prev) =>
      setBracketSetMatchResult(prev, divisionKey, roundIndex, matchIndex, result)
    );
    setScoreTarget(null);
  };

  const scoreBracket = scoreTarget
    ? getAllBracketSetDivisions(bracketData).find((d) => d.key === scoreTarget.divisionKey)?.bracket
    : null;
  const scoreMatch =
    scoreBracket?.rounds[scoreTarget.roundIndex]?.matches[scoreTarget.matchIndex] ?? null;

  const handleSelectRound1Slot = (divisionKey, slotIndex) => {
    if (selectedSwap === null) {
      setSelectedSwap({ division: divisionKey, slot: slotIndex });
      return;
    }
    if (selectedSwap.division === divisionKey && selectedSwap.slot === slotIndex) {
      setSelectedSwap(null);
      return;
    }
    if (selectedSwap.division !== divisionKey) {
      setSelectedSwap({ division: divisionKey, slot: slotIndex });
      return;
    }
    setBracketData((prev) =>
      swapBracketSetSlots(prev, divisionKey, selectedSwap.slot, slotIndex)
    );
    setSelectedSwap(null);
  };

  const handleStartEdit = () => {
    if (isFinished) return;
    editSnapshotRef.current = bracketData;
    setEditMode(true);
    setSelectedSwap(null);
  };

  const handleCancelEdit = () => {
    if (editSnapshotRef.current) setBracketData(editSnapshotRef.current);
    editSnapshotRef.current = null;
    setEditMode(false);
    setSelectedSwap(null);
  };

  const handleSaveEdit = async () => {
    if (!bracketData || selectedId == null) return;
    const saved = await persistBracket(selectedId, bracketData);
    if (!saved) return;
    editSnapshotRef.current = null;
    setEditMode(false);
    setSelectedSwap(null);
    alert(t('tournament.admin.alerts.saved'));
  };

  const handleBracketModeChange = (mode) => {
    if (!bracketData || bracketData.mode === mode) return;

    const confirmed = window.confirm(t('tournament.admin.confirm.changeMode'));
    if (!confirmed) return;

    setBracketData(generateBracketSet(participants, mode, { format: bracketData.format }));
    setSelectedSwap(null);
  };

  const handleFormatChange = (format) => {
    if (!bracketData || (bracketData.format ?? BRACKET_FORMATS.TOURNAMENT) === format) return;
    if (!window.confirm(t('tournament.admin.confirm.changeFormat'))) return;

    const options =
      bracketData.mode === BRACKET_MODES.HEIGHT
        ? { heightRanges: bracketData.heightRanges ?? [], selectedHeightRangeId: bracketData.selectedHeightRangeId }
        : {};
    setBracketData(generateBracketSet(participants, bracketData.mode, { ...options, format }));
    setSelectedSwap(null);
  };

  /** 1:1 경기 편집 (편집 모드에서만, 「저장」을 눌러야 서버에 저장) */
  const handleChangeMatchList = (divisionKey, update) => {
    setBracketData((prev) => updateBracketSetDivision(prev, divisionKey, update));
  };

  const handleApplyHeightRanges = (ranges) => {
    setBracketData((prev) => applyHeightRangesToBracketSet(prev, participants, ranges));
    setSelectedSwap(null);
  };

  const handleHeightRangeSelect = (rangeId) => {
    setBracketData((prev) => setBracketSetSelectedHeightRange(prev, rangeId));
    setSelectedSwap(null);
  };

  const handleResetBracket = () => {
    if (selectedId == null || participants.length < 2 || !bracketData || isFinished) return;

    const confirmed = window.confirm(
      t(matchFormat ? 'tournament.admin.confirm.resetMatches' : 'tournament.admin.confirm.reset')
    );
    if (!confirmed) return;

    setEditMode(false);
    setSelectedSwap(null);
    setBracketNotice(null);
    const options =
      bracketData.mode === BRACKET_MODES.HEIGHT
        ? {
            heightRanges: bracketData.heightRanges ?? [],
            selectedHeightRangeId: bracketData.selectedHeightRangeId,
          }
        : {};
    setBracketData(
      generateBracketSet(participants, bracketData.mode, { ...options, format: bracketData.format })
    );
  };

  const handleFinalize = async () => {
    if (!bracketData || selectedId == null || !canFinalize) return;

    const confirmed = window.confirm(
      t(matchFormat ? 'tournament.admin.confirm.finalizeMatches' : 'tournament.admin.confirm.finalize')
    );
    if (!confirmed) return;

    setFinalizing(true);
    try {
      const saved = await persistBracket(selectedId, bracketData);
      if (!saved) return;
      await finalizeBracket(selectedId);
      const data = await fetchBracket(selectedId);
      if (data) versionRef.current = data.version;
      setBracketStatus('FINISHED');
      alert(t('tournament.admin.alerts.finalized'));
    } catch (error) {
      alert(getApiErrorMessage(error, t('tournament.errors.finalize')));
    } finally {
      setFinalizing(false);
    }
  };

  const handleReopen = async () => {
    if (selectedId == null) return;

    const confirmed = window.confirm(t('tournament.admin.confirm.reopen'));
    if (!confirmed) return;

    setFinalizing(true);
    try {
      const data = await reopenBracket(selectedId);
      versionRef.current = data.version;
      setBracketStatus(data.status);
    } catch (error) {
      alert(getApiErrorMessage(error, t('tournament.errors.reopen')));
    } finally {
      setFinalizing(false);
    }
  };

  const handleOpenDisplay = () => {
    if (selectedId == null) return;
    openBracketDisplayWindow(selectedId);
  };

  return (
    <div className="container container-bracket">
      <div className="page-intro">
        <p className="page-eyebrow">Admin · Tournament Bracket</p>
        <h2 className="page-title">{t('tournament.admin.title')}</h2>
        <p className="page-desc">
          {t('tournament.admin.descBefore')}{' '}
          <Link to="/tournament">/tournament</Link>
          {t('tournament.admin.descAfter')}
        </p>
      </div>

      <TournamentSelector
        tournaments={tournaments}
        loading={tournamentsLoading}
        error={errorText(tournamentsError)}
        onRetry={loadTournaments}
        selectedId={selectedId}
        onSelect={handleSelectTournament}
      />

      {selectedId == null && (
        <p className="panel-empty bracket-empty">{t('tournament.selectPrompt')}</p>
      )}

      {selectedId != null && (
        <section className="bracket-result-section">
          {selectedTournament && (
            <p className="bracket-selected-label">
              {t('tournament.admin.selectedTournament')}: <strong>{selectedTournament.name}</strong>
            </p>
          )}

          {participantsLoading && (
            <div className="list-status bracket-loading">
              <p className="bracket-loading-text">{t('tournament.admin.loadingParticipants')}</p>
              <div className="skeleton-card" />
              <div className="skeleton-card" />
            </div>
          )}

          {!participantsLoading && participantsError && (
            <div className="list-status list-status-error">
              <p>{errorText(participantsError)}</p>
              <button
                type="button"
                className="btn btn-retry"
                onClick={() => loadParticipants(selectedId)}
              >
                {t('common.retry')}
              </button>
            </div>
          )}

          {!participantsLoading && !participantsError && bracketLoading && (
            <div className="list-status bracket-loading">
              <p className="bracket-loading-text">{t('tournament.admin.loadingBracket')}</p>
              <div className="skeleton-card" />
            </div>
          )}

          {!participantsLoading && !participantsError && !bracketLoading && bracketError && (
            <div className="list-status list-status-error">
              <p>{errorText(bracketError)}</p>
              <button
                type="button"
                className="btn btn-retry"
                onClick={() => setReloadKey((k) => k + 1)}
              >
                {t('common.retry')}
              </button>
            </div>
          )}

          {!participantsLoading &&
            !participantsError &&
            !bracketLoading &&
            participants.length > 0 &&
            bracketData && (
              <>
                <div className="bracket-header">
                  <h2 className="bracket-title">
                    <span className="trophy">🏆</span>
                    {selectedTournament?.name} | {getBracketSetSummaryLabel(bracketData)}
                  </h2>
                  <div className="bracket-meta">
                    {selectedTournament?.eventAt && (
                      <div className="bracket-meta-item">
                        📅 {t('tournament.meta.date')}: <strong>{formatEventDate(selectedTournament.eventAt)}</strong>
                      </div>
                    )}
                    {selectedTournament?.location && (
                      <div className="bracket-meta-item">
                        📍 {t('tournament.meta.venue')}: <strong>{selectedTournament.location}</strong>
                      </div>
                    )}
                    <div className="bracket-meta-item">
                      📊 {t('tournament.meta.format')}:{' '}
                      <strong>{t(getBracketFormatLabelKey(bracketData))}</strong>
                    </div>
                    <div className="bracket-meta-item">
                      🥋 {t('tournament.meta.event')}: <strong>{t('tournament.meta.eventName')}</strong>
                    </div>
                  </div>
                  <div className="stats-grid">
                    <div className="stat-box">
                      <div className="stat-label">{t('tournament.stats.total')}</div>
                      <div className="stat-value">{t('common.people', { count: stats.total })}</div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label">{t('tournament.stats.maleFemale')}</div>
                      <div className="stat-value">
                        {stats.male} / {stats.female}
                      </div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label">{t('tournament.stats.avgHeight')}</div>
                      <div className="stat-value">
                        {stats.avgHeight === '-' ? '-' : t('common.cm', { value: stats.avgHeight })}
                      </div>
                    </div>
                    <div className="stat-box">
                      <div className="stat-label">{t('tournament.stats.gyms')}</div>
                      <div className="stat-value">{t('tournament.stats.gymCount', { count: stats.dojos })}</div>
                    </div>
                  </div>
                </div>

                {bracketStatus && (
                  <LiveMatchPanel
                    liveMatches={liveMatches}
                    selectedTournamentId={selectedId}
                    onEnd={handleEndLive}
                  />
                )}

                {bracketNotice && (
                  <p className="bracket-notice">
                    {t(
                      matchFormat
                        ? 'tournament.admin.participantDiffNoticeMatches'
                        : 'tournament.admin.participantDiffNotice',
                      bracketNotice
                    )}
                  </p>
                )}

                <div className={`bracket-toolbar${editMode ? ' bracket-toolbar-edit' : ''}`}>
                  <div className="bracket-toolbar-hint">
                    <p>
                      {t(
                        editMode
                          ? matchFormat
                            ? 'tournament.admin.hint.editMatches'
                            : 'tournament.admin.hint.edit'
                          : isFinished
                            ? 'tournament.admin.hint.finished'
                            : 'tournament.admin.hint.default'
                      )}
                    </p>
                    {saveState === 'error' && saveError ? (
                      <p className="bracket-save-state bracket-save-state-error">
                        {errorText(saveError)}
                      </p>
                    ) : (
                      SAVE_STATE_KEYS[saveState] && (
                        <p className="bracket-save-state">{t(SAVE_STATE_KEYS[saveState])}</p>
                      )
                    )}
                  </div>
                  <div className="bracket-toolbar-actions">
                    {editMode ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-save-bracket"
                          onClick={handleSaveEdit}
                          disabled={saveState === 'saving'}
                        >
                          {t('common.save')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-cancel-edit"
                          onClick={handleCancelEdit}
                        >
                          {t('common.cancel')}
                        </button>
                      </>
                    ) : isFinished ? (
                      <>
                        <span className="bracket-status-badge">
                          {t('tournament.admin.buttons.resultsFinalized')}
                        </span>
                        <button
                          type="button"
                          className="btn btn-reset-bracket"
                          onClick={handleReopen}
                          disabled={finalizing}
                        >
                          {t('tournament.admin.buttons.reopen')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-display-open"
                          onClick={handleOpenDisplay}
                        >
                          {t('tournament.admin.buttons.openDisplay')}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-edit-bracket"
                          onClick={handleStartEdit}
                        >
                          {t('tournament.admin.buttons.editBracket')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-reset-bracket"
                          onClick={handleResetBracket}
                        >
                          {t(
                            matchFormat
                              ? 'tournament.admin.buttons.resetMatches'
                              : 'tournament.admin.buttons.resetBracket'
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-finalize-bracket"
                          onClick={handleFinalize}
                          disabled={!canFinalize || finalizing}
                          title={
                            canFinalize
                              ? ''
                              : t(
                                  matchFormat
                                    ? 'tournament.admin.finalizeDisabledTitleMatches'
                                    : 'tournament.admin.finalizeDisabledTitle'
                                )
                          }
                        >
                          {t(
                            finalizing
                              ? 'tournament.admin.buttons.finalizing'
                              : 'tournament.admin.buttons.finalize'
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-display-open"
                          onClick={handleOpenDisplay}
                        >
                          {t('tournament.admin.buttons.openDisplay')}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className={`bracket-wrap${editMode ? ' bracket-wrap-edit' : ''}`}>
                  {editMode && (
                    <>
                      <p className="bracket-edit-banner">
                        {matchFormat ? (
                          t('tournament.admin.editBannerMatches')
                        ) : (
                          <>
                            {t('tournament.admin.editBanner')}
                            {selectedSwap != null && t('tournament.admin.editBannerSelectSecond')}
                          </>
                        )}
                      </p>
                      <div
                        className="bracket-mode-options"
                        role="radiogroup"
                        aria-label={t('tournament.admin.formatGroupLabel')}
                      >
                        <span className="bracket-mode-label">{t('tournament.admin.formatGroupLabel')}</span>
                        <label className="bracket-mode-option">
                          <input
                            type="radio"
                            name="bracketFormat"
                            checked={!matchFormat}
                            onChange={() => handleFormatChange(BRACKET_FORMATS.TOURNAMENT)}
                          />
                          {t('tournament.admin.formatOptions.tournament')}
                        </label>
                        <label className="bracket-mode-option">
                          <input
                            type="radio"
                            name="bracketFormat"
                            checked={matchFormat}
                            onChange={() => handleFormatChange(BRACKET_FORMATS.MATCHES)}
                          />
                          {t('tournament.admin.formatOptions.matches')}
                        </label>
                      </div>
                      <div
                        className="bracket-mode-options"
                        role="radiogroup"
                        aria-label={t('tournament.admin.modeGroupLabel')}
                      >
                        <span className="bracket-mode-label">{t('tournament.admin.modeGroupLabel')}</span>
                        <label className="bracket-mode-option">
                          <input
                            type="radio"
                            name="bracketMode"
                            checked={bracketData.mode === BRACKET_MODES.COMBINED}
                            onChange={() => handleBracketModeChange(BRACKET_MODES.COMBINED)}
                          />
                          {t('tournament.admin.modeOptions.combined')}
                        </label>
                        <label className="bracket-mode-option">
                          <input
                            type="radio"
                            name="bracketMode"
                            checked={bracketData.mode === BRACKET_MODES.GENDER}
                            onChange={() => handleBracketModeChange(BRACKET_MODES.GENDER)}
                          />
                          {t('tournament.admin.modeOptions.gender')}
                        </label>
                        <label className="bracket-mode-option">
                          <input
                            type="radio"
                            name="bracketMode"
                            checked={bracketData.mode === BRACKET_MODES.HEIGHT}
                            onChange={() => handleBracketModeChange(BRACKET_MODES.HEIGHT)}
                          />
                          {t('tournament.admin.modeOptions.height')}
                        </label>
                      </div>
                    </>
                  )}
                  {bracketData.mode === BRACKET_MODES.HEIGHT && (
                    <BracketHeightRangePanel
                      bracketSet={bracketData}
                      participants={participants}
                      editMode={editMode}
                      onApplyRanges={handleApplyHeightRanges}
                      onSelectRange={handleHeightRangeSelect}
                    />
                  )}
                  <BracketSetView
                    bracketSet={bracketData}
                    divisions={divisions}
                    editMode={editMode}
                    selectedSwap={selectedSwap}
                    onSelectWinner={handleSelectWinner}
                    onSelectRound1Slot={handleSelectRound1Slot}
                    onEditScore={isFinished ? undefined : handleEditScore}
                    liveMatches={liveMatches.filter(
                      (live) => String(live.tournamentId) === String(selectedId)
                    )}
                    onStartLive={isFinished ? undefined : handleStartLive}
                    onEndLive={handleEndLive}
                    participants={participants}
                    onChangeMatchList={editMode ? handleChangeMatchList : undefined}
                  />
                </div>

                {isFinished && (
                  <BracketResultsPanel results={results} bracketSet={bracketData} matchList={matchFormat} />
                )}
              </>
            )}

          {!participantsLoading &&
            !participantsError &&
            !bracketLoading &&
            !bracketError &&
            participants.length > 0 &&
            !bracketData && (
              <p className="panel-empty">
                {t('tournament.admin.notEnoughParticipants', { count: participants.length })}
              </p>
            )}
        </section>
      )}

      <CourtPickerModal
        open={courtTarget != null}
        title={courtTargetTitle}
        liveMatches={liveMatches}
        selectedTournamentId={selectedId}
        courtCount={courtCount}
        onChangeCourtCount={handleChangeCourtCount}
        onPick={handlePickCourt}
        onClose={() => setCourtTarget(null)}
      />

      <MatchScoreModal
        open={scoreMatch != null}
        match={scoreMatch}
        title={
          scoreTarget && scoreBracket
            ? t('tournament.admin.scoreModalTitle', {
                no: getMatchDisplayNumber(scoreBracket, scoreTarget.roundIndex, scoreTarget.matchIndex),
                round: translateRoundLabel(scoreBracket.rounds[scoreTarget.roundIndex].title),
              })
            : ''
        }
        subtitle={
          scoreTarget?.divisionLabel
            ? translateDivisionLabel(scoreTarget.divisionLabel)
            : selectedTournament?.name
        }
        allowDraw={isMatchList(scoreBracket)}
        onSave={handleSaveScore}
        onClose={() => setScoreTarget(null)}
      />
    </div>
  );
}

export default BracketPage;
